import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTypesForBrand, fetchBrandById, fetchBrands } from "../api/BrandAPIs";
import { createType, updateType, deleteType, addTypeStock, updateTypeStock, fetchAssignedCaseStyleIds, setTypeCaseStyles, fetchTypes, setTypeChildTypes, fetchCaseStyleInventory, updateCaseStyleInventory, fetchCaseStyleStocks, fetchCaseStyleSales } from "../api/TypeAPIs";
import { fetchCaseStyles } from "../api/CatalogAPIs";
import { displayAr } from "./Catalog/catalogUi";
import { printTypeNameLabels } from "../utils/qzTray";
import {
  exportBrandAvailableTypesCsv,
  exportAllBrandsAvailableTypesCsv,
  groupAvailableTypesByBrand,
  downloadBrandAvailableTypesPdf,
  downloadAllBrandsAvailableTypesPdf,
  getAvailableTypes,
  sortTypesBySalesDesc,
  getSalesRange,
  getSalesHeatStyle,
} from "../utils/availableTypesReport";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  Layers,
  AlertCircle,
  Tag,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  ChevronUp,
  ChevronDown,
  Printer,
  PackagePlus,
  Package,
  FileDown,
  FileSpreadsheet,
} from "lucide-react";

const mapApiType = (type, brandId, allTypes = []) => {
  const typeId = type.typeId;
  const ownSales = type.sales || 0;
  const children = (allTypes || []).filter((t) => t.mainTypeId === typeId);
  const servedTypes = [
    { id: typeId, name: type.name, sales: ownSales, isParent: true },
    ...children.map((t) => ({
      id: t.typeId,
      name: t.name,
      sales: t.sales || 0,
      isParent: false,
    })),
  ];
  const totalSales = servedTypes.reduce((sum, row) => sum + (row.sales || 0), 0);
  const main = type.mainTypeId
    ? (allTypes || []).find((t) => t.typeId === type.mainTypeId)
    : null;
  return {
    id: typeId,
    name: type.name,
    sales: ownSales,
    ownSales,
    totalSales,
    isAvailable: type.isAvailable !== undefined ? type.isAvailable : true,
    brandId: parseInt(brandId, 10),
    stockQuantity: type.stockQuantity ?? 0,
    lowStockThreshold: type.lowStockThreshold ?? 0,
    unavailableThreshold: type.unavailableThreshold ?? 0,
    isLowStock: !!type.isLowStock,
    lastStockUpdate: type.lastStockUpdate || null,
    mainTypeId: type.mainTypeId || null,
    mainTypeName: main?.name || null,
    isMain: !type.mainTypeId,
    servedTypes,
    servesCount: servedTypes.length,
    childTypeIds: children.map((t) => t.typeId),
  };
};

const numOf = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeStockRow = (row) => ({
  caseStyleId: row?.caseStyleId ?? row?.CaseStyleId ?? null,
  name: row?.name ?? row?.Name ?? "شفاف",
  usesTypeStock: Boolean(row?.usesTypeStock ?? row?.UsesTypeStock),
  stockQuantity: numOf(row?.stockQuantity ?? row?.StockQuantity),
  lowStockThreshold: numOf(row?.lowStockThreshold ?? row?.LowStockThreshold),
  unavailableThreshold: numOf(row?.unavailableThreshold ?? row?.UnavailableThreshold),
  isLowStock: Boolean(row?.isLowStock ?? row?.IsLowStock),
  isInitialized: (row?.isInitialized ?? row?.IsInitialized) !== false,
  availability: row?.availability ?? row?.Availability ?? "available",
});

const fallbackStockRow = (type) => ({
  caseStyleId: null,
  name: "شفاف",
  usesTypeStock: true,
  stockQuantity: numOf(type?.stockQuantity),
  lowStockThreshold: numOf(type?.lowStockThreshold),
  unavailableThreshold: numOf(type?.unavailableThreshold),
  isLowStock: !!type?.isLowStock,
  isInitialized: true,
  availability: numOf(type?.stockQuantity) <= 0 ? "out" : type?.isLowStock ? "low" : "available",
});

const stockRowsOf = (type) => {
  if (Array.isArray(type?.caseStocks) && type.caseStocks.length > 0) {
    return type.caseStocks.map((row) => {
      const normalized = normalizeStockRow(row);
      if (!normalized.usesTypeStock) return normalized;
      return {
        ...normalized,
        stockQuantity: numOf(normalized.stockQuantity, numOf(type?.stockQuantity)),
        lowStockThreshold: numOf(row?.lowStockThreshold ?? row?.LowStockThreshold ?? type?.lowStockThreshold),
        unavailableThreshold: numOf(row?.unavailableThreshold ?? row?.UnavailableThreshold ?? type?.unavailableThreshold),
        isInitialized: true,
      };
    });
  }
  return [fallbackStockRow(type)];
};

const attachCaseStocks = (mappedTypes, stockGroups) => {
  const stockByType = new Map(
    (Array.isArray(stockGroups) ? stockGroups : []).map((group) => [
      group.typeId ?? group.TypeId,
      group.rows || group.Rows || [],
    ])
  );
  return mappedTypes.map((type) => ({
    ...type,
    caseStocks: stockByType.get(type.id) || [],
  }));
};

const attachCaseSales = (mappedTypes, salesGroups) => {
  const salesByOwner = new Map(
    (Array.isArray(salesGroups) ? salesGroups : []).map((group) => [
      group.ownerTypeId ?? group.OwnerTypeId,
      group.models || group.Models || [],
    ])
  );
  return mappedTypes.map((type) => ({
    ...type,
    styleSales: salesByOwner.get(type.id) || [],
  }));
};

const styleSalesTotal = (type) => {
  const models = Array.isArray(type?.styleSales) ? type.styleSales : [];
  if (!models.length) return type?.totalSales ?? type?.sales ?? 0;
  return models.reduce(
    (sum, model) => sum + (model.styles || []).reduce((inner, style) => inner + numOf(style.quantity), 0),
    0
  );
};

const styleName = (row) => displayAr(row?.name) || "شفاف";

const styleLabel = (row) => styleName(row).replace(/\s+/g, " ").trim();

const addChoiceLabel = (row) => (row?.usesTypeStock ? "إضافة عادي" : `إضافة ${styleName(row)}`);

const styleReady = (row) => row?.isInitialized !== false && row?.availability !== "unconfigured";

const styleIsOut = (row) => styleReady(row) && ((row.stockQuantity || 0) <= 0 || row.availability === "out");

const styleIsLow = (row) => styleReady(row) && !styleIsOut(row) && (row.isLowStock || row.availability === "low");

const styleValueClass = (row) => {
  if (!styleReady(row)) return "text-gray-400";
  if (styleIsOut(row)) return "text-red-600";
  if (styleIsLow(row)) return "text-amber-600";
  return "text-blue-700";
};

const typeHasOutStyle = (type) => stockRowsOf(type).some(styleIsOut);
const typeHasLowStyle = (type) => stockRowsOf(type).some(styleIsLow);

const unitsInType = (type) =>
  stockRowsOf(type).reduce((sum, row) => sum + (styleReady(row) ? (row.stockQuantity || 0) : 0), 0);

const patchCaseStockRow = (type, row) => {
  const next = stockRowsOf(type).map((current) =>
    current.caseStyleId === row.caseStyleId ? { ...current, ...row } : current
  );
  const known = next.some((current) => current.caseStyleId === row.caseStyleId);
  return known ? next : [...next, row];
};

const syncTypeStockRow = (type) => ({
  ...type,
  caseStocks: stockRowsOf(type).map((row) =>
    row.usesTypeStock
      ? {
          ...row,
          stockQuantity: type.stockQuantity ?? 0,
          lowStockThreshold: type.lowStockThreshold ?? 0,
          unavailableThreshold: type.unavailableThreshold ?? 0,
          isLowStock: !!type.isLowStock,
          isInitialized: true,
          availability: (type.stockQuantity || 0) <= 0 ? "out" : type.isLowStock ? "low" : "available",
        }
      : row
  ),
});

const styleKey = (row) => {
  if (row?.caseStyleId != null && row.caseStyleId !== "") return `id:${row.caseStyleId}`;
  if (row?.usesTypeStock) return "type-stock";
  return `name:${styleName(row)}`;
};

const METRICS = [
  { id: "sales", label: "المبيعات", short: "مبيعات" },
  { id: "stockQuantity", label: "المخزن", short: "مخزن" },
  { id: "lowStockThreshold", label: "حد الإشعار", short: "إشعار" },
  { id: "unavailableThreshold", label: "حد التوقف", short: "توقف" },
];

const sameStyle = (left, right) => {
  if (left?.caseStyleId && right?.caseStyleId && left.caseStyleId === right.caseStyleId) return true;
  return styleName(left) === styleName(right);
};

const salesLinesFor = (type, styles = []) => {
  const lines = stockRowsOf(type).map((row) => ({
    caseStyleId: row.caseStyleId,
    name: row.name,
    usesTypeStock: !!row.usesTypeStock,
    quantity: 0,
  }));
  (styles || []).forEach((style) => {
    const line = lines.find((item) => sameStyle(item, style));
    if (line) {
      line.quantity += numOf(style.quantity);
      return;
    }
    lines.push({
      caseStyleId: style.caseStyleId,
      name: style.name,
      usesTypeStock: !!style.usesTypeStock,
      quantity: numOf(style.quantity),
    });
  });
  return lines;
};

const SalesLines = ({ lines }) => (
  <div className="space-y-1">
    {lines.map((style) => (
      <p
        key={`${style.usesTypeStock ? "type" : "style"}-${style.caseStyleId ?? styleName(style)}`}
        className="whitespace-nowrap leading-tight text-sm"
      >
        <span className="font-bold text-gray-700">{styleName(style)}</span>
        <span className="font-black text-green-700 tabular-nums"> {numOf(style.quantity)}</span>
      </p>
    ))}
  </div>
);

const styleSalesTotals = (type) => {
  const models = Array.isArray(type?.styleSales) ? type.styleSales : [];
  return salesLinesFor(
    type,
    models.flatMap((model) => model.styles || [])
  );
};

const collectPageStyles = (types) => {
  const map = new Map();
  (types || []).forEach((type) => {
    [...stockRowsOf(type), ...styleSalesTotals(type)].forEach((row) => {
      const name = styleLabel(row);
      const key = styleKey(row);
      const existing = map.get(name);
      if (!existing) {
        map.set(name, {
          key: name,
          name,
          usesTypeStock: !!row.usesTypeStock,
          caseStyleId: row.caseStyleId ?? null,
          keys: [key],
        });
        return;
      }
      if (!existing.keys.includes(key)) existing.keys.push(key);
      if (row.usesTypeStock) existing.usesTypeStock = true;
    });
  });
  const styles = [...map.values()].sort((a, b) => {
    if (a.usesTypeStock !== b.usesTypeStock) return a.usesTypeStock ? -1 : 1;
    return String(a.name).localeCompare(String(b.name), "ar");
  });
  return styles.length
    ? styles
    : [{ key: "شفاف", name: "شفاف", usesTypeStock: true, caseStyleId: null, keys: ["type-stock"] }];
};

const styleKeysOf = (style) => new Set(style?.keys?.length ? style.keys : style?.key ? [style.key] : []);

const rowsForStyle = (type, style) => {
  const keys = styleKeysOf(style);
  return stockRowsOf(type).filter((row) => keys.has(styleKey(row)));
};

const mergedStockRow = (type, style) => {
  const rows = rowsForStyle(type, style);
  if (!rows.length) return null;
  const stockQuantity = rows.reduce((sum, row) => sum + numOf(row.stockQuantity), 0);
  const lowStockThreshold = rows.reduce((sum, row) => sum + numOf(row.lowStockThreshold), 0);
  const unavailableThreshold = rows.reduce((sum, row) => sum + numOf(row.unavailableThreshold), 0);
  const ready = rows.some(styleReady);
  const base = rows.find(styleReady) || rows[0];
  return {
    ...base,
    stockQuantity,
    lowStockThreshold,
    unavailableThreshold,
    isInitialized: ready,
    isLowStock: ready && stockQuantity > 0 && lowStockThreshold > 0 && stockQuantity <= lowStockThreshold,
    availability: !ready ? "unconfigured" : stockQuantity <= 0 ? "out" : "available",
  };
};

const stockMetric = (type, style, field) => {
  const row = mergedStockRow(type, style);
  if (!row) return { text: "—", className: "text-gray-300" };
  if (field === "stockQuantity" && !styleReady(row)) {
    return { text: "غير مهيأ", className: "text-xs font-bold text-gray-400" };
  }
  return {
    text: numOf(row[field]),
    className: `font-black tabular-nums ${field === "stockQuantity" ? styleValueClass(row) : "text-gray-900"}`,
  };
};

const salesMetric = (type, style) => {
  const keys = styleKeysOf(style);
  return styleSalesTotals(type)
    .filter((line) => keys.has(styleKey(line)))
    .reduce((sum, line) => sum + numOf(line.quantity), 0);
};

const styleIsShown = (type, style) => {
  if (!style) return true;
  if (salesMetric(type, style) !== 0) return true;
  const row = mergedStockRow(type, style);
  return !!row && numOf(row.stockQuantity) !== 0;
};

const rowForStyle = (type, style) => (style ? mergedStockRow(type, style) : null);

const typeStyleIsOut = (type, style) => {
  const row = rowForStyle(type, style);
  return !!row && styleIsOut(row);
};

const typeStyleIsLow = (type, style) => {
  const row = rowForStyle(type, style);
  return !!row && styleIsLow(row);
};

const typeStyleBelowStop = (type, style) => {
  const row = rowForStyle(type, style);
  return !!row && styleReady(row) && row.unavailableThreshold > 0 && row.stockQuantity <= row.unavailableThreshold;
};

const applyStockResponse = (type, stock) => ({
  ...type,
  stockQuantity: stock.stockQuantity ?? type.stockQuantity,
  lowStockThreshold: stock.lowStockThreshold ?? type.lowStockThreshold,
  unavailableThreshold: stock.unavailableThreshold ?? type.unavailableThreshold,
  isLowStock: stock.isLowStock ?? type.isLowStock,
  isAvailable: stock.isAvailable ?? type.isAvailable,
  lastStockUpdate: stock.lastStockUpdate || type.lastStockUpdate,
  sales: stock.sales ?? type.sales,
  ownSales: stock.sales ?? type.ownSales ?? type.sales,
  totalSales:
    (stock.sales ?? type.ownSales ?? type.sales ?? 0) +
    Math.max((type.totalSales || 0) - (type.ownSales ?? type.sales ?? 0), 0),
});

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <>
        <div className="animate-pulse hidden md:block">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex gap-4 border-b py-3">
              <div className="w-12 h-12 bg-gray-200 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="animate-pulse flex flex-col gap-4 md:hidden">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3">
              <div className="h-6 bg-gray-200 rounded w-2/3"></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="h-16 bg-gray-200 rounded-xl"></div>
                <div className="h-16 bg-gray-200 rounded-xl"></div>
              </div>
              <div className="h-10 bg-gray-200 rounded-xl"></div>
            </div>
          ))}
        </div>
      </>
    );
  }
  return null;
};

const getTypeStockTone = (type) => {
  const rows = stockRowsOf(type).filter(styleReady);
  const outCount = rows.filter(styleIsOut).length;
  if (outCount > 0) {
    return {
      card: "border-red-200",
      banner: "bg-red-600 text-white",
      bannerText: outCount === rows.length ? "خلص من المخزن" : "فيه نوع كفر خلص",
      stockWrap: "bg-red-50 border-red-100",
      stockValue: "text-red-600",
    };
  }
  if (typeHasLowStyle(type)) {
    return {
      card: "border-amber-200",
      banner: "bg-amber-400 text-amber-950",
      bannerText: "محتاج شراء",
      stockWrap: "bg-amber-50 border-amber-100",
      stockValue: "text-amber-700",
    };
  }
  return {
    card: type.isAvailable ? "border-purple-100" : "border-gray-200",
    banner: null,
    bannerText: "",
    stockWrap: "bg-blue-50 border-blue-100",
    stockValue: "text-blue-700",
  };
};

const TypeMobileCard = ({
  type,
  style,
  isLoading,
  printingId,
  isPrintingAll,
  onToggleAvailability,
  onAddStock,
  onPrint,
  onEdit,
  onDelete,
  onOpenSales,
}) => {
  const tone = !style
    ? getTypeStockTone(type)
    : typeStyleIsOut(type, style)
      ? { card: "border-red-200", banner: "bg-red-600 text-white", bannerText: "خلص من المخزن" }
      : typeStyleIsLow(type, style)
        ? { card: "border-amber-200", banner: "bg-amber-400 text-amber-950", bannerText: "محتاج شراء" }
        : { card: type.isAvailable ? "border-purple-100" : "border-gray-200", banner: null, bannerText: "" };
  const isPrinting = printingId === type.id;

  return (
    <article className={`rounded-2xl border bg-white shadow-sm overflow-hidden ${tone.card}`}>
      {tone.banner && (
        <div className={`px-4 py-1.5 text-center text-sm font-bold ${tone.banner}`}>
          {tone.bannerText}
        </div>
      )}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-[1.15rem] font-bold text-gray-900 leading-snug break-words">
            <button
              type="button"
              onClick={() => onOpenSales?.(type)}
              className="text-right hover:text-purple-700 underline decoration-purple-200 underline-offset-4"
            >
              {type.name}
            </button>
          </h3>
          <span className="shrink-0 font-mono text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-lg">
            #{type.id}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
            نوع رئيسي
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
            يخدم {type.servesCount || 1} أنواع
          </span>
        </div>
        {(type.servedTypes || []).length > 1 && (
          <p className="text-xs text-gray-600 leading-relaxed">
            {(type.servedTypes || []).map((s) => s.name).join(" · ")}
          </p>
        )}

        {style && (
          <div className="grid grid-cols-4 gap-1 rounded-xl border border-gray-100 px-2 py-2 text-center">
            <div>
              <p className="text-[11px] font-medium text-green-700">مبيعات</p>
              <button
                type="button"
                onClick={() => onOpenSales?.(type)}
                className="text-lg font-black tabular-nums text-green-700"
              >
                {salesMetric(type, style)}
              </button>
            </div>
            {["stockQuantity", "lowStockThreshold", "unavailableThreshold"].map((field) => {
              const metric = stockMetric(type, style, field);
              const label = METRICS.find((item) => item.id === field)?.short;
              return (
                <div key={field}>
                  <p className="text-[11px] font-medium text-gray-500">{label}</p>
                  <p className={`text-xl ${metric.className}`}>{metric.text}</p>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={() => onToggleAvailability(type)}
          disabled={isLoading}
          className={`w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${
            type.isAvailable
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          {type.isAvailable ? <CheckCircle size={16} /> : <XCircle size={16} />}
          {type.isAvailable ? "متاح" : "غير متاح"}
        </button>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onAddStock(type)}
            disabled={isLoading}
            className="bg-blue-600 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          >
            <PackagePlus size={16} />
            ستوك
          </button>
          <button
            type="button"
            onClick={() => onPrint(type)}
            disabled={isLoading || isPrinting || isPrintingAll}
            className="bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          >
            <Printer size={16} className={isPrinting ? "animate-pulse" : ""} />
            {isPrinting ? "..." : "طباعة"}
          </button>
          <button
            type="button"
            onClick={() => onEdit(type)}
            disabled={isLoading}
            className="bg-amber-500 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          >
            <Edit size={16} />
            تعديل
          </button>
          <button
            type="button"
            onClick={() => onDelete(type.id)}
            disabled={isLoading}
            className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          >
            <Trash2 size={16} />
            حذف
          </button>
        </div>
      </div>
    </article>
  );
};

const TypesPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [brandData, setBrandData] = useState(null);
  const [newType, setNewType] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [activeStyleKey, setActiveStyleKey] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [isPrintingAll, setIsPrintingAll] = useState(false);
  const [addingStockFor, setAddingStockFor] = useState(null);
  const [addStockStyleId, setAddStockStyleId] = useState(null);
  const [addStockQty, setAddStockQty] = useState("");
  const [caseStyles, setCaseStyles] = useState([]);
  const [styleInventory, setStyleInventory] = useState([]);
  const [styleDrafts, setStyleDrafts] = useState({});
  const [savingStyleId, setSavingStyleId] = useState(null);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isHeatmapOpen, setIsHeatmapOpen] = useState(false);
  const [catalogTypes, setCatalogTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [childPickerScope, setChildPickerScope] = useState("brand");
  const [childPickerSearch, setChildPickerSearch] = useState("");
  const [salesDetailType, setSalesDetailType] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [brandResponse, typesResponse, stylesResponse, allTypesResponse, brandsResponse, stockGroups, salesGroups] =
          await Promise.all([
            fetchBrandById(brandId),
            fetchTypesForBrand(brandId),
            fetchCaseStyles().catch(() => []),
            fetchTypes().catch(() => []),
            fetchBrands().catch(() => []),
            fetchCaseStyleStocks(brandId).catch(() => []),
            fetchCaseStyleSales(brandId).catch(() => []),
          ]);

        setBrandData(brandResponse);
        setCaseStyles(Array.isArray(stylesResponse) ? stylesResponse : []);
        setBrands(Array.isArray(brandsResponse) ? brandsResponse : []);

        const catalog = Array.isArray(allTypesResponse) ? allTypesResponse : [];
        setCatalogTypes(
          catalog.map((type) => mapApiType(type, type.brandId, catalog))
        );

        if (typesResponse && typesResponse.length > 0) {
          const source = catalog.length ? catalog : typesResponse;
          setTypes(
            attachCaseSales(
              attachCaseStocks(
                typesResponse.map((type) => mapApiType(type, brandId, source)),
                stockGroups
              ),
              salesGroups
            ).sort((a, b) => styleSalesTotal(b) - styleSalesTotal(a))
          );
        } else {
          setTypes([]);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError(err.message || "فشل في تحميل البيانات");
      } finally {
        setIsLoading(false);
      }
    };

    if (brandId) {
      loadData();
    }
  }, [brandId]);

  const handleInputChange = (e, setState, state) => {
    const { name, value, type: inputType, checked } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: inputType === "checkbox" ? checked : value,
    }));
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      const createPayload = {
        ...newType,
        brandId: parseInt(brandId)
      };
      const created = await createType(createPayload);
      
      const newTypeItem = mapApiType(created, brandId);
      
      setTypes(prevTypes => 
        [...prevTypes, newTypeItem].sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0))
      );
      setNewType(null);
    } catch (err) {
      console.error("Error creating type:", err);
      alert("فشل في إنشاء النوع: " + err.message);
    }
  };

  const handleUpdateType = async (e) => {
    e.preventDefault();
    try {
      const updatePayload = {
        ...editingType,
        brandId: parseInt(brandId)
      };
      await updateType(editingType.id, updatePayload);
      await setTypeCaseStyles(editingType.id, editingType.caseStyleIds || []);
      const selectedChildren = editingType.childTypeIds || [];
      const catalog = catalogTypes.length ? catalogTypes : types;
      const moving = selectedChildren.filter((childId) => {
        const current = catalog.find((t) => t.id === childId);
        return current?.mainTypeId && current.mainTypeId !== editingType.id;
      });
      if (moving.length > 0) {
        const names = moving
          .map((id) => catalog.find((t) => t.id === id)?.name || id)
          .join("، ");
        const ok = window.confirm(
          `الأنواع التالية تابعة لنوع رئيسي آخر وسيتم تغيير مالك المخزون:\n${names}\nهل تريد المتابعة؟`
        );
        if (!ok) return;
      }
      await setTypeChildTypes(editingType.id, selectedChildren);
      let nextType = {
        ...editingType,
        name: editingType.name,
        isAvailable: editingType.isAvailable,
        brandId: parseInt(brandId, 10)
      };
      if (
        editingType.stockQuantity !== undefined ||
        editingType.lowStockThreshold !== undefined ||
        editingType.unavailableThreshold !== undefined
      ) {
        const stock = await updateTypeStock(
          editingType.id,
          Number(editingType.stockQuantity),
          Number(editingType.lowStockThreshold),
          Number(editingType.unavailableThreshold)
        );
        nextType = applyStockResponse(nextType, stock);
      }

      setTypes(prevTypes =>
        prevTypes.map(type =>
          type.id === editingType.id ? { ...type, ...nextType } : type
        ).sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0))
      );
      setEditingType(null);
      await refreshData();
    } catch (err) {
      console.error("Error updating type:", err);
      alert("فشل في تحديث النوع: " + err.message);
    }
  };

  const handleToggleAvailability = async (type) => {
    try {
      const updatedTypePayload = { 
        ...type, 
        isAvailable: !type.isAvailable,
        brandId: parseInt(brandId)
      };
      await updateType(type.id, updatedTypePayload);
      
      setTypes(prevTypes =>
        prevTypes.map(t => 
          t.id === type.id
            ? { ...t, isAvailable: !t.isAvailable }
            : t
        )
      );
    } catch (err) {
      console.error("Error updating type availability:", err);
      alert("فشل في تحديث حالة التوافر: " + err.message);
    }
  };

  const handleDeleteType = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا النوع؟")) {
      try {
        await deleteType(id);
        setTypes(prevTypes => prevTypes.filter((type) => type.id !== id));
      } catch (err) {
        console.error("Error deleting type:", err);
        alert("فشل في حذف النوع: " + err.message);
      }
    }
  };

  const openEditType = async (type) => {
    let assigned = [];
    let inventory = [];
    try {
      assigned = await fetchAssignedCaseStyleIds(type.id);
    } catch (_) {
      assigned = [];
    }
    if (type.isMain) {
      try {
        inventory = await fetchCaseStyleInventory(type.id);
      } catch (_) {
        inventory = [];
      }
    }
    const drafts = {};
    for (const row of inventory) {
      drafts[row.caseStyleId] = {
        stockQuantity: row.stockQuantity ?? 0,
        lowStockThreshold: row.lowStockThreshold ?? 0,
        unavailableThreshold: row.unavailableThreshold ?? 0,
      };
    }
    setStyleInventory(Array.isArray(inventory) ? inventory : []);
    setStyleDrafts(drafts);
    setChildPickerScope("brand");
    setChildPickerSearch("");
    setEditingType({
      id: type.id,
      name: type.name,
      sales: type.sales,
      isAvailable: type.isAvailable,
      brandId: type.brandId,
      mainTypeId: type.mainTypeId || null,
      isMain: type.isMain,
      stockQuantity: type.stockQuantity ?? 0,
      lowStockThreshold: type.lowStockThreshold ?? 0,
      unavailableThreshold: type.unavailableThreshold ?? 0,
      caseStyleIds: Array.isArray(assigned) ? assigned : [],
      childTypeIds: type.childTypeIds || [],
    });
  };

  const saveStyleInventory = async (style) => {
    if (!editingType?.isMain) return;
    const draft = styleDrafts[style.caseStyleId] || {};
    setSavingStyleId(style.caseStyleId);
    try {
      await setTypeCaseStyles(editingType.id, editingType.caseStyleIds || []);
      const saved = await updateCaseStyleInventory(editingType.id, {
        caseStyleId: style.caseStyleId,
        stockQuantity: Number(draft.stockQuantity) || 0,
        lowStockThreshold: Number(draft.lowStockThreshold) || 0,
        unavailableThreshold: Number(draft.unavailableThreshold) || 0,
      });
      setStyleInventory((prev) => {
        const next = prev.filter((row) => row.caseStyleId !== style.caseStyleId);
        return [...next, saved];
      });
      setTypes((prev) =>
        prev.map((type) => {
          if (type.id !== editingType.id) return type;
          const withRow = { ...type, caseStocks: patchCaseStockRow(type, saved) };
          if (!saved?.usesTypeStock) return withRow;
          return syncTypeStockRow(applyStockResponse(withRow, saved));
        })
      );
      toast.success(`تم حفظ مخزون ${displayAr(style.name)}`);
    } catch (err) {
      toast.error(err.message || "فشل حفظ مخزون نوع الكفر");
    } finally {
      setSavingStyleId(null);
    }
  };

  const openAddStock = (type) => {
    const rows = stockRowsOf(type);
    const preferred = rows.find((row) => row.usesTypeStock) || rows[0];
    setAddingStockFor(type);
    setAddStockStyleId(preferred?.caseStyleId ?? null);
    setAddStockQty("");
  };

  const refreshData = async () => {
    try {
      setIsLoading(true);
      const [typesResponse, allTypesResponse, stockGroups, salesGroups] = await Promise.all([
        fetchTypesForBrand(brandId),
        fetchTypes().catch(() => []),
        fetchCaseStyleStocks(brandId).catch(() => []),
        fetchCaseStyleSales(brandId).catch(() => []),
      ]);
      const catalog = Array.isArray(allTypesResponse) ? allTypesResponse : [];
      setCatalogTypes(catalog.map((type) => mapApiType(type, type.brandId, catalog)));

      if (typesResponse && typesResponse.length > 0) {
        const source = catalog.length ? catalog : typesResponse;
        setTypes(
          attachCaseSales(
            attachCaseStocks(
              typesResponse.map((type) => mapApiType(type, brandId, source)),
              stockGroups
            ),
            salesGroups
          ).sort((a, b) => styleSalesTotal(b) - styleSalesTotal(a))
        );
      } else {
        setTypes([]);
      }
    } catch (err) {
      console.error("Error refreshing data:", err);
      setError(err.message || "فشل في تحديث البيانات");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    if (!addingStockFor) return;
    const qty = Number(addStockQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("أدخل عدد قطع أكبر من صفر");
      return;
    }
    const rows = stockRowsOf(addingStockFor);
    const selected = rows.find((row) => row.caseStyleId === addStockStyleId) || rows[0];
    const styleId = selected && !selected.usesTypeStock ? selected.caseStyleId : null;
    try {
      const stock = await addTypeStock(addingStockFor.id, qty, styleId);
      setTypes((prev) =>
        prev.map((type) => {
          if (type.id !== addingStockFor.id) return type;
          if (stock?.usesTypeStock === false) {
            return { ...type, caseStocks: patchCaseStockRow(type, { ...stock, isInitialized: true }) };
          }
          return syncTypeStockRow(applyStockResponse(type, stock));
        })
      );
      toast.success(`تمت إضافة ${qty} لـ ${selected?.usesTypeStock ? "عادي" : styleName(selected)}`);
      setAddingStockFor(null);
      setAddStockStyleId(null);
      setAddStockQty("");
    } catch (err) {
      console.error("Error adding stock:", err);
      toast.error(err.message || "فشل إضافة المخزون");
    }
  };

  // Filter types based on search term
  const mainTypes = types.filter((type) => type.isMain);
  const childSearchHits = types.filter((type) => {
    if (type.isMain || !searchTerm) return false;
    const term = searchTerm.toLowerCase();
    return type.name.toLowerCase().includes(term) || type.id.toString().includes(term);
  });
  const pageStyles = collectPageStyles(mainTypes).filter((style) =>
    mainTypes.some((type) => styleIsShown(type, style))
  );
  const activeStyle = pageStyles.find((style) => style.key === activeStyleKey) || pageStyles[0] || null;
  const filteredTypes = mainTypes.filter((type) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        type.name.toLowerCase().includes(term) ||
        type.id.toString().includes(term) ||
        (type.servedTypes || []).some(
          (served) =>
            String(served.name || "").toLowerCase().includes(term) ||
            String(served.id).includes(term)
        );
      if (!matchesSearch) return false;
    }
    if (activeStyle && !styleIsShown(type, activeStyle)) return false;
    if (!activeStyle || stockFilter === "all") return true;
    if (stockFilter === "restock") return typeStyleIsLow(type, activeStyle);
    if (stockFilter === "out") return typeStyleIsOut(type, activeStyle);
    if (stockFilter === "belowStop") return typeStyleBelowStop(type, activeStyle);
    return true;
  });
  const styleRestockCount = activeStyle
    ? mainTypes.filter((type) => styleIsShown(type, activeStyle) && typeStyleIsLow(type, activeStyle)).length
    : 0;
  const styleOutCount = activeStyle
    ? mainTypes.filter((type) => styleIsShown(type, activeStyle) && typeStyleIsOut(type, activeStyle)).length
    : 0;

  const restockCount = mainTypes.filter((t) => typeHasLowStyle(t)).length;
  const outCount = mainTypes.filter((t) => typeHasOutStyle(t)).length;
  const unitsOnHand = mainTypes.reduce((sum, t) => sum + unitsInType(t), 0);
  const availableTypes = sortTypesBySalesDesc(getAvailableTypes(types));
  const { min: minAvailableSales, max: maxAvailableSales } = getSalesRange(availableTypes);
  const availableSalesTotal = availableTypes.reduce((sum, type) => sum + (type.sales || 0), 0);

  const handleDownloadBrandReport = () => {
    try {
      const count = exportBrandAvailableTypesCsv(brandData?.name, types);
      toast.success(`تم تحميل تقرير ${count} نوع متاح`);
    } catch (err) {
      toast.warning(err.message || "فشل تحميل التقرير");
    }
  };

  const handleDownloadBrandPdf = async () => {
    try {
      setIsDownloadingPdf(true);
      await downloadBrandAvailableTypesPdf(brandData?.name, types);
      toast.success("تم تحميل ملف PDF على جهازك");
    } catch (err) {
      toast.warning(err.message || "فشل تحميل PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const loadAllBrandsAvailableGroups = async () => {
    const [brands, allTypes] = await Promise.all([fetchBrands(), fetchTypes()]);
    return groupAvailableTypesByBrand(brands, allTypes);
  };

  const handleDownloadAllBrandsReport = async () => {
    try {
      setIsExportingAll(true);
      const grouped = await loadAllBrandsAvailableGroups();
      const count = exportAllBrandsAvailableTypesCsv(grouped);
      toast.success(`تم تحميل تقرير ${count} نوع متاح لكل البراندات`);
    } catch (err) {
      toast.error(err.message || "فشل تحميل تقرير كل البراندات");
    } finally {
      setIsExportingAll(false);
    }
  };

  const handleDownloadAllBrandsPdf = async () => {
    try {
      setIsDownloadingPdf(true);
      const grouped = await loadAllBrandsAvailableGroups();
      await downloadAllBrandsAvailableTypesPdf(grouped);
      toast.success("تم تحميل ملف PDF لكل البراندات");
    } catch (err) {
      toast.error(err.message || "فشل تحميل PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handlePrintType = async (type) => {
    setPrintingId(type.id);
    try {
      await printTypeNameLabels(type.name);
      toast.success(`تم طباعة: ${type.name}`);
    } catch (err) {
      console.error("Print type failed:", err);
      toast.error(err.message || "فشل الطباعة");
    } finally {
      setPrintingId(null);
    }
  };

  const handlePrintAllTypes = async () => {
    const toPrint = types
      .filter((t) => (t.sales || 0) > 10 && t.name)
      .slice()
      .sort((a, b) =>
        a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true })
      );
    const names = toPrint.map((t) => t.name);
    if (names.length === 0) {
      toast.warning("لا توجد أنواع بمبيعات أكثر من 10 للطباعة");
      return;
    }
    if (
      !window.confirm(
        `طباعة الأنواع ذات المبيعات أكثر من 10؟\n\n${names.length} ملصق بالترتيب الأبجدي`
      )
    ) {
      return;
    }

    setIsPrintingAll(true);
    try {
      const count = await printTypeNameLabels(names);
      toast.success(`تم طباعة ${count} ملصق (مبيعات > 10 · أبجدي)`);
    } catch (err) {
      console.error("Print all types failed:", err);
      toast.error(err.message || "فشل الطباعة");
    } finally {
      setIsPrintingAll(false);
    }
  };

  if (!brandId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4" dir="rtl">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-500" size={64} />
            <h1 className="text-2xl font-bold text-red-600">خطأ في التحميل</h1>
            <p className="mt-2 text-gray-600">معرف العلامة التجارية مفقود</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-3 pb-24 md:p-8 md:pb-8" dir="rtl">
      <ToastContainer position="top-center" rtl />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-5 md:mb-8 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Layers className="text-purple-600 shrink-0" size={26} />
              <h1 className="text-xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent truncate">
                أنواع {brandData ? brandData.name : "جاري التحميل..."}
              </h1>
            </div>
            <button
              onClick={() => navigate("/brands")}
              className="shrink-0 flex items-center gap-1 bg-white/80 backdrop-blur-sm text-gray-700 px-3 py-2 md:px-6 md:py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 text-sm md:text-base"
            >
              <ChevronRight size={18} />
              <span className="sm:hidden">رجوع</span>
              <span className="hidden sm:inline">العودة للعلامات التجارية</span>
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
            <button
              onClick={handleDownloadBrandReport}
              disabled={isLoading || availableTypes.length === 0}
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 bg-indigo-600 text-white px-2 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-md hover:bg-indigo-700 hover:shadow-xl transition-all duration-300 disabled:opacity-50 text-xs sm:text-sm font-bold"
              title="تحميل CSV للأنواع المتاحة ومبيعاتها"
            >
              <FileDown size={16} />
              <span className="sm:hidden">تقرير</span>
              <span className="hidden sm:inline">تقرير المتاح</span>
            </button>
            <button
              onClick={handlePrintAllTypes}
              disabled={isLoading || isPrintingAll || filteredTypes.length === 0}
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 bg-emerald-600 text-white px-2 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-md hover:bg-emerald-700 hover:shadow-xl transition-all duration-300 disabled:opacity-50 text-xs sm:text-sm font-bold"
              title="طباعة الأنواع ذات المبيعات أكثر من 10"
            >
              <Printer size={16} className={isPrintingAll ? "animate-pulse" : ""} />
              {isPrintingAll ? "..." : (
                <>
                  <span className="sm:hidden">طباعة</span>
                  <span className="hidden sm:inline">طباعة (مبيعات &gt; 10)</span>
                </>
              )}
            </button>
            <button
              onClick={refreshData}
              disabled={isLoading}
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 bg-white/80 backdrop-blur-sm text-gray-700 px-2 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 disabled:opacity-50 text-xs sm:text-sm font-bold"
            >
              <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
              تحديث
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-3 md:px-6 md:py-4 rounded-2xl mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 md:gap-3">
              <AlertCircle className="text-red-500 shrink-0" size={20} />
              <p className="text-sm md:text-base">{error}</p>
            </div>
            <button
              onClick={refreshData}
              className="text-red-600 hover:text-red-800 text-sm font-bold self-start sm:self-auto"
            >
              إعادة المحاولة
            </button>
          </div>
        )}

        {/* Stats and Actions */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 md:gap-6 mb-5 md:mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-6 border border-white/30">
            <div className="flex items-center gap-2 mb-1 md:hidden">
              <Tag className="text-purple-600" size={16} />
              <h2 className="text-xs font-bold text-gray-600">الأنواع</h2>
            </div>
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Tag className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">عدد الأنواع</h2>
                  <p className="text-sm text-gray-600">جرابات الطباعة</p>
                </div>
              </div>
              <div className="text-4xl font-bold text-purple-600">{types.length}</div>
            </div>
            <div className="text-2xl font-black text-purple-600 md:hidden">{types.length}</div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-6 border border-white/30">
            <div className="flex items-center gap-2 mb-1 md:hidden">
              <Package className="text-blue-600" size={16} />
              <h2 className="text-xs font-bold text-gray-600">قطع المخزن</h2>
            </div>
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Package className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">قطع المخزن</h2>
                  <p className="text-sm text-gray-600">ستوك الطباعة فقط</p>
                </div>
              </div>
              <div className="text-4xl font-bold text-blue-600">{unitsOnHand}</div>
            </div>
            <div className="text-2xl font-black text-blue-600 md:hidden">{unitsOnHand}</div>
          </div>
          <button
            type="button"
            onClick={() => setStockFilter(stockFilter === "restock" ? "all" : "restock")}
            className={`text-right bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-6 border transition ${
              stockFilter === "restock" ? "border-amber-400 ring-2 ring-amber-200" : "border-white/30"
            }`}
          >
            <p className="text-xs md:text-sm text-gray-600">محتاج شراء</p>
            <p className="text-2xl md:text-4xl font-black md:font-bold text-amber-600 mt-0.5 md:mt-1">{restockCount}</p>
          </button>
          <button
            type="button"
            onClick={() => setStockFilter(stockFilter === "out" ? "all" : "out")}
            className={`text-right bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-6 border transition ${
              stockFilter === "out" ? "border-red-400 ring-2 ring-red-200" : "border-white/30"
            }`}
          >
            <p className="text-xs md:text-sm text-gray-600">خلص من المخزن</p>
            <p className="text-2xl md:text-4xl font-black md:font-bold text-red-600 mt-0.5 md:mt-1">{outCount}</p>
          </button>
        </div>

        {!isLoading && availableTypes.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl mb-6 border border-white/30 overflow-hidden">
            <button
              type="button"
              onClick={() => setIsHeatmapOpen((open) => !open)}
              className="w-full flex items-center justify-between gap-3 p-3 md:p-4 text-right hover:bg-white/50 transition"
              aria-expanded={isHeatmapOpen}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-indigo-100 rounded-xl shrink-0">
                  {isHeatmapOpen ? (
                    <ChevronUp className="text-indigo-700" size={20} />
                  ) : (
                    <ChevronDown className="text-indigo-700" size={20} />
                  )}
                </div>
                <div className="min-w-0">
                  <h2 className="text-base md:text-lg font-bold text-gray-800">خريطة مبيعات الأنواع المتاحة</h2>
                  <p className="text-xs md:text-sm text-gray-500 truncate">
                    {availableTypes.length} نوع متاح · إجمالي المبيعات {availableSalesTotal}
                    {availableTypes[0] ? ` · الأعلى ${availableTypes[0].name} (${availableTypes[0].sales || 0})` : ""}
                  </p>
                </div>
              </div>
              <span className="text-sm font-bold text-indigo-600 shrink-0">
                {isHeatmapOpen ? "إخفاء" : "عرض"}
              </span>
            </button>

            {isHeatmapOpen && (
              <div className="px-3 pb-4 md:px-6 md:pb-6">
                <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 mb-4">
                  <button
                    type="button"
                    onClick={handleDownloadBrandPdf}
                    disabled={isDownloadingPdf}
                    className="flex items-center justify-center gap-2 bg-white text-indigo-700 px-4 py-2.5 rounded-xl border border-indigo-200 hover:bg-indigo-50 font-bold text-sm disabled:opacity-50"
                  >
                    <FileDown size={16} />
                    {isDownloadingPdf ? "جاري تحميل PDF..." : "تحميل PDF"}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadAllBrandsReport}
                    disabled={isExportingAll}
                    className="flex items-center justify-center gap-2 bg-white text-gray-700 px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 font-bold text-sm disabled:opacity-50"
                  >
                    <FileSpreadsheet size={16} />
                    {isExportingAll ? "جاري التحميل..." : "CSV كل البراندات"}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadAllBrandsPdf}
                    disabled={isDownloadingPdf}
                    className="flex items-center justify-center gap-2 bg-white text-gray-700 px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 font-bold text-sm disabled:opacity-50"
                  >
                    <FileDown size={16} />
                    {isDownloadingPdf ? "جاري تحميل PDF..." : "PDF كل البراندات"}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-gray-500">
                  <span>أقل مبيعات</span>
                  <div
                    className="h-2 flex-1 max-w-[220px] rounded-full"
                    style={{
                      background: "linear-gradient(to left, hsl(218,72%,90%), hsl(55,85%,62%), hsl(8,82%,52%))",
                    }}
                  />
                  <span>أكثر مبيعات</span>
                  <span className="mr-auto text-gray-400">
                    {minAvailableSales} → {maxAvailableSales}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {availableTypes.map((type) => {
                    const heat = getSalesHeatStyle(type.sales, minAvailableSales, maxAvailableSales);
                    return (
                      <div
                        key={type.id}
                        className="rounded-xl p-2.5 md:p-3 text-center shadow-sm min-h-[72px] md:min-h-[84px] flex flex-col justify-center"
                        style={heat}
                        title={`${type.name}: ${type.sales || 0} مبيعة`}
                      >
                        <div className="font-bold text-xs md:text-sm leading-tight">{type.name}</div>
                        <div className="text-lg md:text-xl font-black mt-1">{type.sales || 0}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-4 md:bg-white/80 md:backdrop-blur-sm md:rounded-2xl md:shadow-xl md:p-6 md:mb-6 md:border md:border-white/30 md:flex md:items-center md:justify-end">
          <button
            onClick={() => setNewType({ name: "", isAvailable: true })}
            disabled={isLoading}
            className="w-full md:w-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-3 md:px-8 md:py-4 rounded-xl shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 font-bold text-base md:text-lg disabled:opacity-50"
          >
            <Plus size={22} />
            إضافة نوع جديد
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-4 mb-4 md:mb-6 border border-white/30">
          <div className="relative mb-3">
            <Search className="absolute right-3 md:right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="بحث باسم النوع أو الرقم..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 md:pr-12 pl-4 py-2.5 md:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all"
            />
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            {[
              { id: "all", label: "الكل" },
              { id: "restock", label: `محتاج شراء (${styleRestockCount})` },
              { id: "out", label: `خلص (${styleOutCount})` },
              { id: "belowStop", label: "تحت حد التوقف" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setStockFilter(f.id)}
                className={`px-3 py-2.5 rounded-xl text-sm font-bold border transition ${
                  stockFilter === f.id
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-purple-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {pageStyles.length > 1 && (
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pageStyles.map((style) => {
              const selected = activeStyle?.key === style.key;
              return (
                <button
                  key={style.key}
                  type="button"
                  onClick={() => setActiveStyleKey(style.key)}
                  className={`rounded-xl px-3 py-3 text-sm md:text-base font-bold border transition ${
                    selected
                      ? "bg-purple-600 text-white border-purple-600 shadow-md"
                      : "bg-white text-gray-800 border-gray-200 hover:bg-purple-50"
                  }`}
                >
                  {style.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Types list */}
        {isLoading ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 md:p-6">
            <SkeletonLoader type="table" />
          </div>
        ) : filteredTypes.length || childSearchHits.length ? (
          <>
            {childSearchHits.length > 0 && (
              <div className="mb-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-950">
                <p className="font-bold mb-2">نتائج بحث في الأنواع التابعة</p>
                <ul className="space-y-1">
                  {childSearchHits.map((hit) => (
                    <li key={hit.id}>
                      <b>{hit.name}</b> — تابع لـ {hit.mainTypeName || hit.mainTypeId} · مالك المخزون: {hit.mainTypeName || hit.mainTypeId}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col gap-3 md:hidden">
              {filteredTypes.map((type) => (
                <TypeMobileCard
                  key={type.id}
                  type={type}
                  style={activeStyle}
                  isLoading={isLoading}
                  printingId={printingId}
                  isPrintingAll={isPrintingAll}
                  onToggleAvailability={handleToggleAvailability}
                  onAddStock={openAddStock}
                  onPrint={handlePrintType}
                  onEdit={openEditType}
                  onDelete={handleDeleteType}
                  onOpenSales={setSalesDetailType}
                />
              ))}
            </div>
            <div className="hidden md:block bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 overflow-x-auto">
              <table className="w-full min-w-[68rem] table-fixed border-collapse text-base [&_th:not(:first-child)]:border-s [&_td:not(:first-child)]:border-s [&_th:not(:first-child)]:border-purple-200 [&_td:not(:first-child)]:border-gray-300">
                <colgroup>
                  <col className="w-[4%]" />
                  <col className="w-[21%]" />
                  <col className="w-[5%]" />
                  <col className="w-[12%]" />
                  <col className="w-[12%]" />
                  <col className="w-[12%]" />
                  <col className="w-[12%]" />
                  <col className="w-[8%]" />
                  <col className="w-[14%]" />
                </colgroup>
                <thead>
                  <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                    <th className="whitespace-nowrap px-1.5 py-3.5 text-center text-base font-bold text-purple-900">#</th>
                    <th className="px-3 py-3.5 text-right text-base font-bold text-purple-900">اسم النوع</th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-base font-bold text-purple-900">يخدم</th>
                    <th className="whitespace-nowrap px-2 py-3.5 text-center text-base font-bold text-purple-900">المبيعات</th>
                    <th className="whitespace-nowrap px-2 py-3.5 text-center text-base font-bold text-purple-900">المخزن</th>
                    <th className="whitespace-nowrap px-2 py-3.5 text-center text-base font-bold text-purple-900">حد الإشعار</th>
                    <th className="whitespace-nowrap px-2 py-3.5 text-center text-base font-bold text-purple-900">حد التوقف</th>
                    <th className="whitespace-nowrap px-1.5 py-3.5 text-center text-base font-bold text-purple-900">الحالة</th>
                    <th className="whitespace-nowrap px-2 py-3.5 text-center text-base font-bold text-purple-900">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTypes.map((type) => (
                    <tr
                      key={type.id}
                      className={`border-b border-gray-200 hover:bg-white/60 transition-colors ${
                        activeStyle && typeStyleIsOut(type, activeStyle)
                          ? "bg-red-50/50"
                          : activeStyle && typeStyleIsLow(type, activeStyle)
                            ? "bg-amber-50/40"
                            : type.isAvailable
                              ? ""
                              : "bg-red-50/30"
                      }`}
                    >
                      <td className="px-1.5 py-3.5 text-center align-top">
                        <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-lg bg-purple-50 px-1 py-1 font-mono text-sm font-bold text-purple-700">
                          {type.id}
                        </span>
                      </td>
                      <td className="px-3 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => setSalesDetailType(type)}
                          className="break-words text-right text-lg font-bold leading-snug text-gray-900 hover:text-purple-700"
                        >
                          {type.name}
                        </button>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-bold text-indigo-800">
                            نوع رئيسي
                          </span>
                        </div>
                        {(type.servedTypes || []).length > 1 && (
                          <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                            {(type.servedTypes || []).map((s) => s.name).join(" · ")}
                          </p>
                        )}
                      </td>
                      <td className="px-2 py-3 text-center align-middle">
                        <span className="text-2xl font-black tabular-nums text-purple-700">{type.servesCount || 1}</span>
                      </td>
                      <td className="px-2 py-3 text-center align-middle">
                        <button
                          type="button"
                          onClick={() => setSalesDetailType(type)}
                          className="text-2xl font-black tabular-nums text-green-700 hover:underline"
                        >
                          {activeStyle ? salesMetric(type, activeStyle) : "—"}
                        </button>
                      </td>
                      {["stockQuantity", "lowStockThreshold", "unavailableThreshold"].map((field) => {
                        const value = activeStyle
                          ? stockMetric(type, activeStyle, field)
                          : { text: "—", className: "text-gray-300" };
                        return (
                          <td key={field} className="px-2 py-3 text-center align-middle">
                            <span className={String(value.className).includes("text-xs") ? value.className : `text-2xl ${value.className}`}>
                              {value.text}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-1 py-4 text-center align-middle">
                        <button
                          onClick={() => handleToggleAvailability(type)}
                          disabled={isLoading}
                          className={`inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-bold transition disabled:opacity-50 ${
                            type.isAvailable
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-red-100 text-red-700 hover:bg-red-200"
                          }`}
                        >
                          {type.isAvailable ? <CheckCircle size={16} /> : <XCircle size={16} />}
                          {type.isAvailable ? "متاح" : "غير متاح"}
                        </button>
                      </td>
                      <td className="px-1.5 py-2.5 align-middle">
                        <div className="grid grid-cols-2 gap-1">
                          <button
                            onClick={() => openAddStock(type)}
                            disabled={isLoading}
                            className="flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-blue-600 px-1.5 py-1 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                            title="إضافة ستوك"
                          >
                            <PackagePlus size={13} />
                            ستوك
                          </button>
                          <button
                            onClick={() => handlePrintType(type)}
                            disabled={isLoading || printingId === type.id || isPrintingAll}
                            className="flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-emerald-600 px-1.5 py-1 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                            title="طباعة"
                          >
                            <Printer size={13} className={printingId === type.id ? "animate-pulse" : ""} />
                            طباعة
                          </button>
                          <button
                            onClick={() => openEditType(type)}
                            disabled={isLoading}
                            className="flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-amber-500 px-1.5 py-1 text-xs font-bold text-white hover:bg-amber-600 disabled:opacity-50"
                            title="تعديل"
                          >
                            <Edit size={13} />
                            تعديل
                          </button>
                          <button
                            onClick={() => handleDeleteType(type.id)}
                            disabled={isLoading}
                            className="flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-red-500 px-1.5 py-1 text-xs font-bold text-white hover:bg-red-600 disabled:opacity-50"
                            title="حذف"
                          >
                            <Trash2 size={13} />
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-10 md:p-16 text-center">
            <Layers className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-xl md:text-2xl text-gray-700 mb-2">لا توجد أنواع</p>
            <p className="text-base md:text-lg text-gray-500">قم بإضافة نوع جديد للبدء</p>
          </div>
        )}

        {/* Create Modal */}
        {newType && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 md:p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-xl md:text-2xl font-bold">إضافة نوع جديد</h2>
                <button
                  onClick={() => setNewType(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateType} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم النوع
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newType.name}
                      onChange={(e) => handleInputChange(e, setNewType, newType)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all"
                      placeholder="أدخل اسم النوع"
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-xl">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      id="isAvailable"
                      checked={newType.isAvailable}
                      onChange={(e) => handleInputChange(e, setNewType, newType)}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="isAvailable" className="font-medium text-gray-700">
                      متاح للعرض
                    </label>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setNewType(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    إنشاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingType && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
              <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 text-white p-4 md:p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-xl md:text-2xl font-bold">تعديل النوع #{editingType.id}</h2>
                <button
                  onClick={() => setEditingType(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdateType} className="p-4 md:p-6 max-h-[70vh] overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم النوع
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editingType.name}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                      placeholder="أدخل اسم النوع"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      رصيد المخزن (الرقم الكامل)
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="stockQuantity"
                      value={editingType.stockQuantity ?? 0}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">للتعديل المباشر لو فيه بضاعة تالفة أو جرد.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-right font-medium text-gray-700 mb-2">
                        حد الإشعار
                      </label>
                      <input
                        type="number"
                        min="0"
                        name="lowStockThreshold"
                        value={editingType.lowStockThreshold ?? 0}
                        onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-right font-medium text-gray-700 mb-2">
                        حد التوقف
                      </label>
                      <input
                        type="number"
                        min="0"
                        name="unavailableThreshold"
                        value={editingType.unavailableThreshold ?? 0}
                        onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    هذا رصيد {displayAr(caseStyles.find((style) => style.usesTypeStock)?.name) || "الكفر الافتراضي"}.
                    لما يوصل لحد التوقف، هذا النوع فقط يتوقف. أنواع الكفر الأخرى لها رصيد مستقل بالأسفل.
                  </p>

                  <div className="p-3 bg-purple-50 rounded-xl">
                    <p className="font-medium text-gray-700 mb-1">الأنواع التي يخدمها هذا الكفر</p>
                    <p className="text-xs text-gray-500 mb-2">
                      النوع الرئيسي نفسه يظهر للعميل، بالإضافة لأي موديلات تابعة تستخدم نفس المخزون.
                    </p>
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setChildPickerScope("brand")}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold border ${
                          childPickerScope === "brand"
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-gray-700 border-purple-200"
                        }`}
                      >
                        نفس البراند
                      </button>
                      <button
                        type="button"
                        onClick={() => setChildPickerScope("all")}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold border ${
                          childPickerScope === "all"
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-gray-700 border-purple-200"
                        }`}
                      >
                        كل البراندات
                      </button>
                    </div>
                    <input
                      type="text"
                      value={childPickerSearch}
                      onChange={(e) => setChildPickerSearch(e.target.value)}
                      placeholder="بحث باسم الموديل..."
                      className="w-full mb-2 p-2.5 border-2 border-purple-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      <label className="flex items-center gap-2 p-2 bg-white rounded-lg border border-purple-100">
                        <input type="checkbox" checked readOnly className="w-5 h-5 text-purple-600 rounded" />
                        <span className="text-sm text-gray-800">{editingType.name} (رئيسي)</span>
                      </label>
                      {(() => {
                        const source = catalogTypes.length ? catalogTypes : types;
                        const selectedIds = editingType.childTypeIds || [];
                        const brandNameOf = (t) =>
                          brands.find((b) => String(b.brandId) === String(t.brandId))?.name || "";
                        const eligible = source.filter((t) => {
                          if (t.id === editingType.id) return false;
                          return !source.some((c) => c.mainTypeId === t.id);
                        });
                        const visible = eligible.filter((t) => {
                          if (selectedIds.includes(t.id)) return true;
                          if (childPickerScope === "brand" && String(t.brandId) !== String(brandId)) {
                            return false;
                          }
                          const term = childPickerSearch.trim().toLowerCase();
                          if (!term) return true;
                          return (
                            t.name.toLowerCase().includes(term) ||
                            String(t.id).includes(term) ||
                            brandNameOf(t).toLowerCase().includes(term)
                          );
                        });
                        if (visible.length === 0) {
                          return (
                            <p className="text-xs text-gray-500 p-2">لا توجد نتائج مطابقة للبحث.</p>
                          );
                        }
                        return visible.map((t) => {
                          const checked = selectedIds.includes(t.id);
                          const otherOwner = t.mainTypeId && t.mainTypeId !== editingType.id;
                          const brandName = brandNameOf(t);
                          return (
                            <label
                              key={t.id}
                              className="flex items-center gap-2 p-2 bg-white rounded-lg border border-purple-100"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const current = editingType.childTypeIds || [];
                                  setEditingType({
                                    ...editingType,
                                    childTypeIds: checked
                                      ? current.filter((id) => id !== t.id)
                                      : [...current, t.id],
                                  });
                                }}
                                className="w-5 h-5 text-purple-600 rounded"
                              />
                              <span className="text-sm text-gray-800">
                                {t.name}
                                {brandName ? ` · ${brandName}` : ""}
                                {otherOwner ? ` (تابع حالياً لـ ${t.mainTypeName || t.mainTypeId})` : ""}
                              </span>
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      id="editIsAvailable"
                      checked={editingType.isAvailable}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
                    />
                    <label htmlFor="editIsAvailable" className="font-medium text-gray-700">
                      متاح للعرض
                    </label>
                  </div>

                  <div className="p-3 bg-indigo-50 rounded-xl">
                    <p className="font-medium text-gray-700 mb-2">أنواع الكفر المتاحة</p>
                    {caseStyles.length === 0 ? (
                      <p className="text-sm text-gray-500">لا توجد أنواع كفر. أضفها من صفحة الكتالوج أولاً.</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {caseStyles.map((style) => {
                          const checked = (editingType.caseStyleIds || []).includes(style.caseStyleId);
                          return (
                            <label
                              key={style.caseStyleId}
                              className="flex items-center gap-2 p-2 bg-white rounded-lg border border-indigo-100"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const current = editingType.caseStyleIds || [];
                                  setEditingType({
                                    ...editingType,
                                    caseStyleIds: checked
                                      ? current.filter((id) => id !== style.caseStyleId)
                                      : [...current, style.caseStyleId],
                                  });
                                }}
                                className="w-5 h-5 text-indigo-600 rounded"
                              />
                              <span className="text-sm text-gray-800">
                                {displayAr(style.name)}
                                {style.isActive === false ? " (معطّل)" : ""}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {editingType.isMain && (
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="font-medium text-gray-700 mb-1">مخزون أنواع الكفر</p>
                      <p className="text-xs text-gray-500 mb-2">
                        الكفر الافتراضي يستخدم الرصيد أعلاه. أي نوع آخر يبدأ بدون رصيد ولا يُباع قبل حفظ رقمه هنا. الموديلات التابعة لنفس المظلة تشارك هذا الرصيد.
                      </p>
                      <div className="space-y-3">
                        {caseStyles
                          .filter((style) => (editingType.caseStyleIds || []).includes(style.caseStyleId) && !style.usesTypeStock)
                          .map((style) => {
                            const saved = styleInventory.find((row) => row.caseStyleId === style.caseStyleId);
                            const draft = styleDrafts[style.caseStyleId] || {
                              stockQuantity: saved?.stockQuantity ?? 0,
                              lowStockThreshold: saved?.lowStockThreshold ?? 0,
                              unavailableThreshold: saved?.unavailableThreshold ?? 0,
                            };
                            const status = saved?.availability || "unconfigured";
                            const statusLabel = status === "available" ? "متاح" : status === "low" ? "منخفض" : status === "out" ? "غير متوفر" : "غير مهيأ";
                            return (
                              <div key={style.caseStyleId} className="bg-white border border-slate-200 rounded-xl p-2">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <span className="text-sm font-bold text-gray-800">{displayAr(style.name)}</span>
                                  <span className="text-[11px] font-bold text-slate-600">{statusLabel}</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <label className="block text-right">
                                    <span className="block text-[11px] font-bold text-gray-600 mb-1">الرصيد</span>
                                    <input
                                      type="number"
                                      min="0"
                                      aria-label="الرصيد"
                                      value={draft.stockQuantity}
                                      onChange={(e) => setStyleDrafts((prev) => ({
                                        ...prev,
                                        [style.caseStyleId]: { ...draft, stockQuantity: e.target.value },
                                      }))}
                                      className="w-full p-2 border rounded-lg text-sm"
                                    />
                                  </label>
                                  <label className="block text-right">
                                    <span className="block text-[11px] font-bold text-gray-600 mb-1">حد الإشعار</span>
                                    <input
                                      type="number"
                                      min="0"
                                      aria-label="حد الإشعار"
                                      value={draft.lowStockThreshold}
                                      onChange={(e) => setStyleDrafts((prev) => ({
                                        ...prev,
                                        [style.caseStyleId]: { ...draft, lowStockThreshold: e.target.value },
                                      }))}
                                      className="w-full p-2 border rounded-lg text-sm"
                                    />
                                  </label>
                                  <label className="block text-right">
                                    <span className="block text-[11px] font-bold text-gray-600 mb-1">حد التوقف</span>
                                    <input
                                      type="number"
                                      min="0"
                                      aria-label="حد التوقف"
                                      value={draft.unavailableThreshold}
                                      onChange={(e) => setStyleDrafts((prev) => ({
                                        ...prev,
                                        [style.caseStyleId]: { ...draft, unavailableThreshold: e.target.value },
                                      }))}
                                      className="w-full p-2 border rounded-lg text-sm"
                                    />
                                  </label>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => saveStyleInventory(style)}
                                  disabled={savingStyleId === style.caseStyleId}
                                  className="mt-2 w-full bg-slate-800 text-white text-sm font-bold py-2 rounded-lg disabled:opacity-50"
                                >
                                  {savingStyleId === style.caseStyleId ? "جار الحفظ..." : "حفظ رصيد هذا النوع"}
                                </button>
                              </div>
                            );
                          })}
                        {caseStyles.filter((style) => (editingType.caseStyleIds || []).includes(style.caseStyleId) && !style.usesTypeStock).length === 0 && (
                          <p className="text-xs text-gray-500">لا توجد أنواع كفر إضافية معينة. عيّن نوعًا ثم احفظ رصيده.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setEditingType(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    حفظ التغييرات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {salesDetailType && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 md:p-5 flex justify-between items-center">
                <h2 className="text-lg md:text-xl font-bold leading-snug">مبيعات {salesDetailType.name}</h2>
                <button
                  type="button"
                  onClick={() => setSalesDetailType(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="p-4 md:p-5 overflow-y-auto space-y-3">
                <div className="rounded-xl bg-green-50 border border-green-100 px-4 py-3">
                  <div className={`grid gap-2 ${styleSalesTotals(salesDetailType).length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {styleSalesTotals(salesDetailType).map((style) => (
                      <div key={`${style.usesTypeStock ? "type" : "style"}-${style.caseStyleId ?? styleName(style)}`} className="text-center">
                        <p className="text-xs font-medium text-green-700">إجمالي {styleName(style)}</p>
                        <p className="text-3xl font-black text-green-700 tabular-nums">{numOf(style.quantity)}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-green-800/80 mt-2 text-center">بدون الملغي والسبام والاستبدال</p>
                </div>
                <div className="space-y-2">
                  {(salesDetailType.styleSales?.length
                    ? salesDetailType.styleSales
                    : (salesDetailType.servedTypes || []).map((row) => ({
                        typeId: row.id,
                        name: row.name,
                        isParent: row.isParent,
                        styles: [{ caseStyleId: null, name: "شفاف", quantity: row.sales || 0 }],
                      }))
                  ).map((model) => (
                    <div key={model.typeId ?? model.name} className="p-3 rounded-xl border border-gray-100 bg-gray-50 space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-bold text-gray-900">{model.name}</p>
                        <p className="text-xs text-gray-500">{model.isParent ? "النوع الرئيسي" : "نوع تابع"}</p>
                      </div>
                      <SalesLines lines={salesLinesFor(salesDetailType, model.styles || [])} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {addingStockFor && (() => {
          const addRows = stockRowsOf(addingStockFor);
          const selectedAdd =
            addRows.find((row) => row.caseStyleId === addStockStyleId && (addStockStyleId != null || row.usesTypeStock)) ||
            addRows.find((row) => row.usesTypeStock) ||
            addRows[0];
          const currentAdd = styleReady(selectedAdd) ? (selectedAdd?.stockQuantity || 0) : 0;
          const closeAddStock = () => {
            setAddingStockFor(null);
            setAddStockStyleId(null);
            setAddStockQty("");
          };
          return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-lg md:text-xl font-bold leading-snug">إضافة ستوك · {addingStockFor.name}</h2>
                <button
                  onClick={closeAddStock}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleAddStock} className="p-6 space-y-4">
                {addRows.length > 1 && (
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-gray-700">هتضيف على أنهي نوع؟</p>
                    <div className="grid grid-cols-1 gap-2">
                      {addRows.map((row) => {
                        const active = selectedAdd?.caseStyleId === row.caseStyleId && selectedAdd?.usesTypeStock === row.usesTypeStock;
                        return (
                          <button
                            key={`${row.usesTypeStock ? "type" : "style"}-${row.caseStyleId ?? "default"}`}
                            type="button"
                            onClick={() => setAddStockStyleId(row.caseStyleId ?? null)}
                            className={`flex items-center justify-between gap-3 rounded-xl border-2 px-3 py-2.5 text-right font-bold transition ${
                              active ? "border-blue-600 bg-blue-50 text-blue-800" : "border-gray-200 text-gray-700 hover:border-blue-300"
                            }`}
                          >
                            <span>{addChoiceLabel(row)}</span>
                            <span className={`text-sm ${styleValueClass(row)}`}>
                              {styleReady(row) ? (row.stockQuantity ?? 0) : "غير مهيأ"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <p className="text-gray-600">
                  رصيد {styleName(selectedAdd)} الحالي: <span className="font-bold text-blue-700">{currentAdd}</span>
                </p>
                <div>
                  <label className="block text-right font-medium text-gray-700 mb-2">
                    عدد القطع اللي ضفتها للمخزن
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={addStockQty}
                    onChange={(e) => setAddStockQty(e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 transition-all"
                    placeholder="مثال: 30"
                    required
                    autoFocus
                  />
                </div>
                {Number(addStockQty) > 0 && (
                  <p className="text-sm text-gray-500">
                    الإجمالي الجديد: {currentAdd + Number(addStockQty)}
                  </p>
                )}
                <div className="flex justify-center gap-4 pt-2">
                  <button
                    type="button"
                    onClick={closeAddStock}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold transition"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 font-bold transition"
                  >
                    <PackagePlus size={20} />
                    إضافة
                  </button>
                </div>
              </form>
            </div>
          </div>
          );
        })()}
      </div>
      {/* Floating Button to Scroll to Top - أضف هذا الزر الجديد */}
      <div className="fixed bottom-5 left-5 md:bottom-6 md:right-6 md:left-auto z-40">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
          title="العودة إلى الأعلى"
        >
          <ChevronUp size={24} />
        </button>
      </div>
    </div>
  );
};

export default TypesPage;